// ui-kit: hand-built
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { RiMoreFill } from '@remixicon/react';

import { classNames } from '../../internal/classNames';
import { OverlayBoundary } from '../../internal/OverlayBoundary';
import { useOnClickOutside } from '../../internal/useOnClickOutside';

import Button, { type ButtonAction, type ButtonSize, type ButtonVariant } from '../Button/Button';
import type { ChipColor } from '../Chip/Chip';
import IconAction from '../IconAction/IconAction';
import type { IconSource } from '../Icon/Icon';
import Menu from '../Menu/Menu';
import MenuItem, { type MenuItemTypology } from '../Menu/MenuItem';

export type MoreOptionsAlign = 'left' | 'right';
export type MoreOptionsVerticalAlign = 'top' | 'bottom';
/** `icon` for Alert/Table; `button` for PageHeader and denser action rows. */
export type MoreOptionsTrigger = 'icon' | 'button';

type MoreOptionsContextValue = {
    closeMenu: () => void;
};

const moreOptionsContext = createContext<MoreOptionsContextValue>({
    closeMenu: () => {},
});

export type MoreOptionsProps = {
    title?: string;
    children: ReactNode;
    className?: string;
    align?: MoreOptionsAlign;
    verticalAlign?: MoreOptionsVerticalAlign;
    icon?: IconSource;
    /** Trigger control. Defaults to IconAction (`icon`). */
    trigger?: MoreOptionsTrigger;
    /** Used when `trigger` is `button`. */
    variant?: ButtonVariant;
    /** Used when `trigger` is `button`. */
    size?: ButtonSize;
    /** Used when `trigger` is `button`. */
    action?: ButtonAction;
};

export type MoreOptionsItemProps = {
    label: ReactNode;
    onClick: (ev: MouseEvent<HTMLDivElement>) => void;
    icon?: IconSource;
    destructive?: boolean;
    disabled?: boolean;
    /** Defaults to `action`. Use `selectTag` for chip-labeled rows. */
    typology?: Extract<MenuItemTypology, 'action' | 'selectTag'>;
    /** Chip color when `typology` is `selectTag`. */
    tagColor?: ChipColor;
};

const MENU_PADDING_FROM_TRIGGER = 4;

function MoreOptionsPortal({
    anchorRef,
    children,
    onClose,
    align = 'right',
    verticalAlign = 'bottom',
}: {
    anchorRef: RefObject<HTMLElement | null>;
    children: ReactNode;
    onClose: () => void;
    align?: MoreOptionsAlign;
    verticalAlign?: MoreOptionsVerticalAlign;
}) {
    const portalRef = useRef<HTMLDivElement>(null!);
    const [settings, setSettings] = useState({ top: 0, left: 0 });

    useOnClickOutside(portalRef, onClose);

    useEffect(() => {
        const updatePosition = () => {
            const btnRect = anchorRef.current?.getBoundingClientRect();
            const menuEl = portalRef.current?.firstElementChild as HTMLElement | null;
            const menuRect = menuEl?.getBoundingClientRect();

            if (!btnRect || !menuRect) {
                return;
            }

            const canOpenLeft = btnRect.right - menuRect.width > 0;
            const canOpenRight = btnRect.left + menuRect.width < window.innerWidth;
            const canOpenDown = btnRect.bottom + menuRect.height < window.innerHeight;
            const canOpenUp = btnRect.top - menuRect.height > 0;

            setSettings({
                top:
                    (verticalAlign === 'bottom' && canOpenDown) || !canOpenUp
                        ? btnRect.bottom + window.scrollY + MENU_PADDING_FROM_TRIGGER
                        : btnRect.top -
                          menuRect.height +
                          window.scrollY -
                          MENU_PADDING_FROM_TRIGGER,
                left:
                    (align === 'right' && canOpenLeft) || !canOpenRight
                        ? btnRect.right - menuRect.width + window.scrollX
                        : btnRect.left + window.scrollX,
            });
        };

        updatePosition();

        const controller = new AbortController();
        window.addEventListener('scroll', updatePosition, { signal: controller.signal });
        window.addEventListener('resize', updatePosition, { signal: controller.signal });

        return () => {
            controller.abort();
        };
    }, [anchorRef, align, verticalAlign, onClose]);

    return createPortal(
        <div
            ref={portalRef}
            className='ui-more-options__portal'
            style={{ top: settings.top, left: settings.left }}
            onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <OverlayBoundary>{children}</OverlayBoundary>
        </div>,
        document.body,
    );
}

function MoreOptionsItem({
    label,
    onClick,
    icon,
    destructive = false,
    disabled = false,
    typology = 'action',
    tagColor,
}: MoreOptionsItemProps) {
    const { closeMenu } = useContext(moreOptionsContext);

    return (
        <MenuItem
            typology={typology}
            role='menuitem'
            label={label}
            icon={icon}
            tagColor={tagColor}
            destructive={destructive}
            disabled={disabled}
            onClick={(e) => {
                if (disabled) {
                    return;
                }
                closeMenu();
                onClick(e);
            }}
        />
    );
}

function MoreOptions({
    title = 'More actions',
    children,
    className = '',
    align = 'right',
    verticalAlign = 'bottom',
    icon = RiMoreFill,
    trigger = 'icon',
    variant = 'floating',
    size = 'medium',
    action = 'primary',
}: MoreOptionsProps) {
    const buttonRef = useRef<HTMLButtonElement>(null!);
    const focusTriggerOnClose = useRef(false);
    const [open, setOpen] = useState(false);

    const closeMenu = useCallback(() => setOpen(false), []);
    const closeMenuAndFocusTrigger = useCallback(() => {
        focusTriggerOnClose.current = true;
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open && focusTriggerOnClose.current) {
            focusTriggerOnClose.current = false;
            buttonRef.current.focus();
        }
    }, [open]);

    const handleMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            closeMenuAndFocusTrigger();
            return;
        }
        if (e.key === 'Tab') {
            closeMenu();
        }
    };

    const handleTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen((prev) => !prev);
    };

    const triggerControl =
        trigger === 'button' ? (
            <Button
                ref={buttonRef}
                icon={icon}
                title={title}
                variant={variant}
                size={size}
                action={action}
                aria-expanded={open}
                aria-haspopup='menu'
                onClick={handleTriggerClick}
            />
        ) : (
            <IconAction
                ref={buttonRef}
                icon={icon}
                title={open ? undefined : title}
                aria-label={title}
                aria-expanded={open}
                aria-haspopup='menu'
                onClick={handleTriggerClick}
            />
        );

    return (
        <div
            className={classNames({
                'ui-more-options': true,
                'ui-more-options--open': open,
                [className]: Boolean(className),
            })}
            data-test-component='ui-kit/MoreOptions'>
            {triggerControl}

            {open ? (
                <MoreOptionsPortal
                    anchorRef={buttonRef}
                    onClose={closeMenu}
                    align={align}
                    verticalAlign={verticalAlign}>
                    <moreOptionsContext.Provider value={{ closeMenu: closeMenuAndFocusTrigger }}>
                        <Menu
                            role='menu'
                            autoFocusItem
                            onKeyDown={handleMenuKeyDown}
                            className='ui-more-options__menu'
                            data-test-component='ui-kit/MoreOptions/Menu'
                            data-test-align={align}
                            data-test-vertical-align={verticalAlign}>
                            {children}
                        </Menu>
                    </moreOptionsContext.Provider>
                </MoreOptionsPortal>
            ) : null}
        </div>
    );
}

MoreOptions.Item = MoreOptionsItem;
MoreOptions.displayName = 'MoreOptions';

export default MoreOptions;
